<template>
  <v-card class="py-1 px-2 mb-1 pw-quote-position">
    <v-row no-gutters>
      <v-col
        v-if="showOptionalCheckbox"
        cols="auto"
        class="d-flex align-center justify-center mr-1"
      >
        <v-row no-gutters>
          <sf-checkbox
            v-if="quoteItem.isOptional"
            v-model="isPickedOptional"
            class="my-1"
            :disabled="!editable || isReadonly"
            :aria-label="
              isPickedOptional ? $t('quotes.deselectOptionalItem') : $t('quotes.selectOptionalItem')
            "
          />
          <sf-checkbox
            v-else
            v-model="checked"
            class="my-1"
            disabled
            :aria-label="$t('quotes.noOptionalPosition')"
          />
        </v-row>
      </v-col>
      <v-col class="mr-1 mr-md-2">
        <v-row no-gutters>
          <v-col cols="auto" class="d-flex align-center mr-2">
            <product-image-gallery
              :images="quoteItem.productImageUrls"
              :alt-text="quoteItem.productTitle"
              :size="100"
              hide-arrows
              hover-effect
            />
          </v-col>
          <v-col class="d-flex flex-column justify-center">
            <span
              class="text-body-1"
              :class="{ 'text-disabled': !isQuoteItemSelected }"
              v-html="sanitizedProductTitle"
            />
            <div
              v-if="showSubscriptionText || showDatasheetButton"
              class="d-flex flex-row align-center"
            >
              <subscription-period
                v-if="showSubscriptionText && quoteItem.price"
                class="text-body-1 text-medium-emphasis"
                :class="{ 'text-disabled': !isQuoteItemSelected }"
                :subscription-model="quoteItem.price?.subscriptionModel || undefined"
              />
              <sf-text-button
                v-if="showDatasheetButton"
                :class="showSubscriptionText ? 'ml-2' : 'ml-n2'"
                :loading="loading"
                :color="isQuoteItemSelected ? 'primary' : 'grey'"
                @click="loadDatasheet"
              >
                {{ $t('datasheet') }}
              </sf-text-button>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col class="hidden-md-and-up" cols="12">
        <v-divider class="my-1" />
      </v-col>
      <v-col cols="12" md="auto" align-self="center">
        <v-row no-gutters justify="end">
          <v-col
            cols="auto"
            class="d-flex justify-center align-center"
            :class="[{ 'mr-md-2': quoteItem.totalPrice }]"
          >
            <quantity-stepper
              v-if="editable && quoteItem.isOptional && quoteItem.isQuantityEditCustomer"
              class="mr-1 quantity-stepper"
              :quantity="quantity"
              :disabled="isReadonly"
              :class="{ 'text-disabled': !isQuoteItemSelected }"
              @change-quantity="onChangeQuantity"
            />
            <sf-heading v-else ref="quantity" :class="{ 'text-disabled': !isQuoteItemSelected }">{{
              quoteItem.quantity
            }}</sf-heading>
            <sf-heading :class="{ 'text-disabled': !isQuoteItemSelected }">x</sf-heading>
            <price-and-discount :item="quoteItem" />
          </v-col>
          <v-col
            v-if="quoteItem.totalPrice"
            cols="auto"
            style="width: 120px"
            class="d-flex flex-column justify-center align-end ml-3 ml-md-0 mr-2"
          >
            <sf-heading ref="total-price" :class="{ 'text-disabled': !isQuoteItemSelected }">
              {{ formatMoneyShowZeroPrice(quoteItem.totalPrice, false) }}
            </sf-heading>
            <billing-period
              v-if="quoteItem.price && quoteItem.price.subscriptionModel"
              class="text-body-1 text-medium-emphasis"
              :class="{ 'text-disabled': !isQuoteItemSelected }"
              :subscription-model="quoteItem.price.subscriptionModel"
            />
            <span
              v-if="quoteItem.vatPercent != undefined && quoteItem.totalPrice.value > 0"
              ref="vat-rate"
              class="text-body-1 text-medium-emphasis"
              :class="{ 'text-disabled': !isQuoteItemSelected }"
            >
              {{
                isGrossQuote
                  ? $t('includingVatShort', [getVatRateText(quoteItem.vatPercent, portalSettings)])
                  : $t('excludingVatShort', [getVatRateText(quoteItem.vatPercent, portalSettings)])
              }}
            </span>
          </v-col>
          <v-col class="d-flex justify-end">
            <sf-icon-button
              v-if="!isGuest"
              :icon="mdiOpenInNew"
              :aria-label="$t('openProductPage')"
              target="_blank"
              :color="disableLinkToProductPage ? 'primary' : 'grey'"
              :disabled="!disableLinkToProductPage"
              :to="
                disableLinkToProductPage
                  ? {
                      name: 'Product',
                      params: { id: quoteItem.productId }
                    }
                  : undefined
              "
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup lang="ts">
import BillingPeriod from '@/components/BillingPeriod.vue'
import SfIconButton from '@/components/button/SfIconButton.vue'
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfCheckbox from '@/components/input/SfCheckbox.vue'
import QuantityStepper from '@/components/QuantityStepper.vue'
import SubscriptionPeriod from '@/components/SubscriptionPeriod.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import { isGuest, isQuotesPortal, isQuotesPortalOrGuest } from '@/helpers'
import useAuthentication from '@/modules/authentication/useAuthentication'
import { getVatRateText } from '@/modules/portalSettings/helpers'
import usePortalSettings from '@/modules/portalSettings/usePortalSettings'
import PriceAndDiscount from '@/modules/priceAndDiscount/PriceAndDiscount.vue'
import ProductImageGallery from '@/modules/product/components/ProductImageGallery.vue'
import { formatMoneyShowZeroPrice } from '@/modules/product/helpers'
import useQuote from '@/modules/quote/useQuote'
import useQuoteItemDatasheet from '@/modules/quote/useQuoteItemDatasheet'
import { QuoteItem } from '@/types/index'
import { mdiOpenInNew } from '@mdi/js'
import DOMPurify from 'dompurify'
import { storeToRefs } from 'pinia'
import { computed, PropType, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  quoteItem: {
    type: Object as PropType<QuoteItem>,
    required: true
  },
  editable: {
    type: Boolean,
    default: true
  },
  isGrossQuote: {
    type: Boolean,
    required: true
  },
  showOptionalCheckbox: {
    type: Boolean,
    default: false
  }
})

const currentRoute = useRoute()
const { loading } = storeToRefs(useQuoteItemDatasheet())
const { portalSettings } = storeToRefs(usePortalSettings())
const isQuoteItemSelected = computed(
  () => !props.quoteItem.isOptional || (props.quoteItem.isOptional && isPickedOptional.value)
)
const isPickedOptional = ref(props.quoteItem.isPickedOptional)
const disableLinkToProductPage = computed(
  () => !isQuotesPortal.value && props.quoteItem.productId && props.quoteItem.isInShopAssortment
)
const showSubscriptionText = computed(
  () => props.quoteItem.price && props.quoteItem.price.subscriptionModel
)
const showDatasheetButton = computed(
  () => isQuotesPortalOrGuest.value && props.quoteItem.hasDatasheet
)
const isReadonly = computed(() => useAuthentication().state.value.isReadonly)
const checked = ref(true)

watch(isPickedOptional, (newValue: boolean) => {
  useQuote().updateQuoteItemIsPickedOptional(props.quoteItem.id, newValue)
})

const quantity = ref(props.quoteItem.quantity)

watch(
  () => props.quoteItem,
  (newValue) => {
    quantity.value = newValue.quantity
  }
)

const onChangeQuantity = (newQuantity: number) => {
  quantity.value = newQuantity
  useQuote().updateQuoteItemQuantity(props.quoteItem.id, newQuantity)
}

const loadDatasheet = () => {
  useQuoteItemDatasheet().loadDatasheet(currentRoute.params.id as string, props.quoteItem.id)
}

const sanitizedProductTitle = computed(() => {
  return DOMPurify.sanitize(props.quoteItem.productTitle)
})
</script>
